import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ApplicationPortal = () => {
  const [applicationStep, setApplicationStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      linkedIn: ''
    },
    experience: {
      currentRole: '',
      experience: '',
      industry: '',
      skills: [],
      resume: null
    },
    preferences: {
      position: '',
      department: '',
      startDate: '',
      salary: '',
      workArrangement: ''
    },
    additional: {
      coverLetter: '',
      portfolio: null,
      referral: '',
      questions: ''
    }
  });

  const positionOptions = [
    { value: 'junior-consultant', label: 'Junior Business Analyst' },
    { value: 'consultant', label: 'Management Consultant' },
    { value: 'senior-consultant', label: 'Senior Management Consultant' },
    { value: 'technical-analyst', label: 'Technical Solutions Analyst' },
    { value: 'technical-consultant', label: 'Technical Solutions Architect' },
    { value: 'operations-manager', label: 'Operations Excellence Manager' },
    { value: 'data-scientist', label: 'Data Science Consultant' },
    { value: 'principal', label: 'Principal Consultant' }
  ];

  const departmentOptions = [
    { value: 'consulting', label: 'Strategy & Operations' },
    { value: 'technology', label: 'Digital Innovation' },
    { value: 'operations', label: 'Process Excellence' },
    { value: 'data', label: 'Data & Analytics' }
  ];

  const experienceOptions = [
    { value: '0-2', label: '0-2 years' },
    { value: '2-5', label: '2-5 years' },
    { value: '5-10', label: '5-10 years' },
    { value: '10+', label: '10+ years' }
  ];

  const workArrangementOptions = [
    { value: 'office', label: 'Office-based' },
    { value: 'hybrid', label: 'Hybrid (3 days office)' },
    { value: 'remote', label: 'Remote-first' },
    { value: 'flexible', label: 'Flexible arrangement' }
  ];

  const applicationSteps = [
    { number: 1, title: 'Personal Information', icon: 'User' },
    { number: 2, title: 'Experience & Skills', icon: 'Briefcase' },
    { number: 3, title: 'Position Preferences', icon: 'Target' },
    { number: 4, title: 'Additional Information', icon: 'FileText' }
  ];

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleFileUpload = (section, field, file) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: file
      }
    }));
  };

  const nextStep = () => {
    if (applicationStep < 4) {
      setApplicationStep(applicationStep + 1);
    }
  };

  const prevStep = () => {
    if (applicationStep > 1) {
      setApplicationStep(applicationStep - 1);
    }
  };

  const handleSubmit = () => {
    // Mock submission
    alert('Application submitted successfully! We will review your application and get back to you within 5 business days.');
  };

  const renderStepContent = () => {
    switch (applicationStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="First Name"
                type="text"
                required
                value={formData.personalInfo.firstName}
                onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                placeholder="Enter your first name"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Input
                label="Last Name"
                type="text"
                required
                value={formData.personalInfo.lastName}
                onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                placeholder="Enter your last name"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Email Address"
                type="email"
                required
                value={formData.personalInfo.email}
                onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                placeholder="your.email@example.com"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Input
                label="Phone Number"
                type="tel"
                required
                value={formData.personalInfo.phone}
                onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Current Location"
                type="text"
                required
                value={formData.personalInfo.location}
                onChange={(e) => handleInputChange('personalInfo', 'location', e.target.value)}
                placeholder="City, State"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Input
                label="LinkedIn Profile"
                type="url"
                value={formData.personalInfo.linkedIn}
                onChange={(e) => handleInputChange('personalInfo', 'linkedIn', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Current Role/Title"
                type="text"
                required
                value={formData.experience.currentRole}
                onChange={(e) => handleInputChange('experience', 'currentRole', e.target.value)}
                placeholder="e.g., Senior Business Analyst"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Select
                label="Years of Experience"
                required
                options={experienceOptions}
                value={formData.experience.experience}
                onChange={(value) => handleInputChange('experience', 'experience', value)}
                placeholder="Select experience level"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
            <Input
              label="Industry Background"
              type="text"
              value={formData.experience.industry}
              onChange={(e) => handleInputChange('experience', 'industry', e.target.value)}
              placeholder="e.g., Financial Services, Healthcare, Technology"
              className="border border-gray-300 rounded-lg" // Added border
            />
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Resume/CV *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-brand-cyan/50 transition-colors duration-300">
                <Icon name="Upload" size={32} className="text-gray-500 mx-auto mb-2" />
                <p className="text-gray-700 mb-2">
                  Drag and drop your resume here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload('experience', 'resume', e.target.files[0])}
                  className="hidden"
                  id="resume-upload"
                />
                <label htmlFor="resume-upload">
                  <Button variant="outline" size="sm" className="cursor-pointer">
                    Choose File
                  </Button>
                </label>
                <p className="text-xs text-gray-600 mt-2">
                  Supported formats: PDF, DOC, DOCX (Max 5MB)
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Select
                label="Preferred Position"
                required
                options={positionOptions}
                value={formData.preferences.position}
                onChange={(value) => handleInputChange('preferences', 'position', value)}
                placeholder="Select position of interest"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Select
                label="Department Preference"
                required
                options={departmentOptions}
                value={formData.preferences.department}
                onChange={(value) => handleInputChange('preferences', 'department', value)}
                placeholder="Select department"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Earliest Start Date"
                type="date"
                value={formData.preferences.startDate}
                onChange={(e) => handleInputChange('preferences', 'startDate', e.target.value)}
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Input
                label="Salary Expectations"
                type="text"
                value={formData.preferences.salary}
                onChange={(e) => handleInputChange('preferences', 'salary', e.target.value)}
                placeholder="e.g., $80,000 - $100,000"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
            <Select
              label="Work Arrangement Preference"
              options={workArrangementOptions}
              value={formData.preferences.workArrangement}
              onChange={(value) => handleInputChange('preferences', 'workArrangement', value)}
              placeholder="Select work arrangement"
              className="border border-gray-300 rounded-lg" // Added border
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Cover Letter
              </label>
              <textarea
                value={formData.additional.coverLetter}
                onChange={(e) => handleInputChange('additional', 'coverLetter', e.target.value)}
                placeholder="Tell us why you're interested in joining Ons Mettle and what unique value you would bring to our team..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Portfolio/Work Samples (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors duration-300">
                <Icon name="FolderOpen" size={32} className="text-gray-500 mx-auto mb-2" />
                <p className="text-gray-700 mb-2">
                  Upload portfolio, case studies, or work samples
                </p>
                <input
                  type="file"
                  accept=".pdf,.ppt,.pptx,.zip"
                  onChange={(e) => handleFileUpload('additional', 'portfolio', e.target.files[0])}
                  className="hidden"
                  id="portfolio-upload"
                />
                <label htmlFor="portfolio-upload">
                  <Button variant="outline" size="sm" className="cursor-pointer">
                    Choose File
                  </Button>
                </label>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Referral Source"
                type="text"
                value={formData.additional.referral}
                onChange={(e) => handleInputChange('additional', 'referral', e.target.value)}
                placeholder="How did you hear about this opportunity?"
                className="border border-gray-300 rounded-lg" // Added border
              />
              <Input
                label="Questions for Us"
                type="text"
                value={formData.additional.questions}
                onChange={(e) => handleInputChange('additional', 'questions', e.target.value)}
                placeholder="Any questions about the role or company?"
                className="border border-gray-300 rounded-lg" // Added border
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading mb-6">
            Application Portal
          </h2>
          <p className="text-xl text-gray-700 font-body">
            Ready to join our team? Complete your application in just a few steps.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            {applicationSteps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                  applicationStep >= step.number
                    ? 'bg-brand-cyan border-brand-cyan text-white'
                    : 'glass-morph bg-white/60 border-gray-300 text-gray-600'
                }`}>
                  {applicationStep > step.number ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <Icon name={step.icon} size={20} />
                  )}
                </div>
                {index < applicationSteps.length - 1 && (
                  <div className={`hidden sm:block w-16 lg:w-24 h-0.5 mx-4 transition-colors duration-300 ${
                    applicationStep > step.number ? 'bg-primary' : 'bg-gray-300'
                  }`}></div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center">
            <h3 className="text-xl font-semibold text-primary font-heading mb-2">
              Step {applicationStep}: {applicationSteps[applicationStep - 1].title}
            </h3>
            <p className="text-gray-700">
              {applicationStep === 1 && "Let's start with your basic information"}
              {applicationStep === 2 && "Tell us about your professional background"}
              {applicationStep === 3 && "What type of role are you looking for?"}
              {applicationStep === 4 && "Any additional information you'd like to share?"}
            </p>
          </div>
        </div>

        {/* Application Form */}
        <div className="glass-morph bg-white/80 backdrop-blur-md border border-gray-300 rounded-2xl shadow-lg p-8">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-300">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={applicationStep === 1}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Previous
            </Button>

            <div className="flex gap-2">
              {applicationSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    applicationStep === index + 1 ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {applicationStep < 4 ? (
              <Button
                variant="default"
                onClick={nextStep}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next Step
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                className="bg-accent hover:bg-accent/90"
                iconName="Send"
                iconPosition="right"
              >
                Submit Application
              </Button>
            )}
          </div>
        </div>

        {/* Application Tips */}
        <div className="mt-12 glass-morph bg-blue-100/50 backdrop-blur-md rounded-xl border border-gray-300 p-6">
          <h3 className="text-lg font-semibold text-gray-800 font-heading mb-4">
            Application Tips
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={20} className="text-brand-cyan mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1 font-heading">Highlight Your Impact</h4>
                <p className="text-sm text-gray-700 font-body">
                  Focus on quantifiable achievements and the business impact of your work.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="Target" size={20} className="text-brand-purple mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1 font-heading">Be Specific</h4>
                <p className="text-sm text-gray-700">
                  Provide concrete examples of projects, technologies, and methodologies you've used.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="Users" size={20} className="text-accent mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Show Collaboration</h4>
                <p className="text-sm text-gray-700">
                  Demonstrate your ability to work with diverse teams and stakeholders.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="TrendingUp" size={20} className="text-accent mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Growth Mindset</h4>
                <p className="text-sm text-gray-700">
                  Share examples of how you've learned new skills and adapted to challenges.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationPortal;